import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { ResultsFilter } from "@/components/ResultsFilter";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import {
  getCCPs,
  createCCP,
  updateCCP,
  deleteCCP,
  getHaccpLogs,
  createHaccpLog,
  updateHaccpLog,
  resolveHaccpLog,
  addCorrectiveAction,
  deleteHaccpLog,
  downloadHaccpCsv,
  downloadPdf,
  uploadFile,
  fetchFileBlobUrl,
  getOperators,
  fmt,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { formatDateTime } from "@/lib/dates";
import { usePagination } from "@/hooks/usePagination";
import { TablePagination } from "@/components/TablePagination";
import {
  Plus,
  Trash2,
  Pencil,
  ShieldCheck,
  ShieldAlert,
  Download,
  FileText,
  ClipboardCheck,
  Clock,
  Filter,
  Upload,
  Camera,
  ListChecks,
  BellRing,
  Printer,
  UserRound,
} from "lucide-react";

const MEAS_TYPES = ["temperature", "ph", "time", "number"];
const FIELD_TYPES = ["number", "temperature", "ph", "time", "boolean", "select", "checklist", "text", "date", "datetime", "photo"];
const fmtFieldValue = (f, v) => {
  if (f.type === "datetime") return String(v).slice(0, 16).replace("T", " ");
  if (f.type === "date") return String(v).slice(0, 10);
  return v;
};
const NUMERIC_TYPES = ["number", "temperature", "ph", "time"];
const statusMap = { ok: "ok", warn: "warn", critical: "bad" };

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);

const emptyCCP = {
  id: null,
  name: "",
  process_step: "",
  measurement_type: "temperature",
  unit: "°C",
  critical_min: "",
  critical_max: "",
  warning_min: "",
  warning_max: "",
  frequency: "Per batch",
  frequency_hours: "",
  frequency_weeks: "",
  reminder_enabled: false,
  reminder_mode: "daily",
  recurrence_interval: 1,
  recurrence_unit: "hours",
  recurrence_weekdays: [],
  reminder_time: "",
  task_operator_id: "",
  escalation_minutes: "",
  due_at: "",
  notes: "",
  fields: [],
  __custom: false,
};

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6]; // Mon … Sun
const DAY_KEYS = ["tasks.dayMon", "tasks.dayTue", "tasks.dayWed", "tasks.dayThu", "tasks.dayFri", "tasks.daySat", "tasks.daySun"];

/** Map a stored CCP (possibly legacy: recurring/once/frequency_hours) onto the task-style repeat model. */
function normalizeReminder(c) {
  let mode = c.reminder_mode || "recurring";
  let interval = c.recurrence_interval;
  let unit = c.recurrence_unit;
  if (mode === "recurring") { mode = "custom"; unit = "hours"; interval = interval || c.frequency_hours || 1; }
  else if (mode === "once") mode = "none";
  else if (mode === "weekly" && (c.frequency_weeks || 1) > 1 && !interval) { mode = "custom"; unit = "weeks"; interval = c.frequency_weeks; }
  return {
    enabled: c.reminder_enabled ?? !!c.frequency_hours,
    mode,
    interval: interval || 1,
    unit: unit || "hours",
    weekdays: c.recurrence_weekdays || [],
    time: ["daily", "working_days", "weekdays"].includes(mode) ? (c.reminder_time || "") : "",
    task_operator_id: c.task_operator_id || "",
    escalation_minutes: c.escalation_minutes || "",
    due_at: c.due_at || "",
  };
}
const ESCALATION_OPTIONS = [15, 30, 60, 120, 240, 480];
const DAY_MODES = ["daily", "working_days", "weekdays"];

/** Short schedule label for lists (mirrors Tasks' recurrenceLabel). */
function reminderLabel(t, c) {
  const r = normalizeReminder(c);
  if (!r.enabled) return null;
  if (r.mode === "none") return r.due_at ? t("haccp.due", { date: String(r.due_at).slice(0, 16).replace("T", " ") }) : t("tasks.recur_none");
  if (r.mode === "custom") {
    const n = r.interval;
    const unit = t(r.unit === "weeks" ? "tasks.unitWeeksPlain" : r.unit === "days" ? "tasks.unitDaysPlain" : "haccp.unitHoursPlain");
    return t("tasks.recurEvery", { interval: `${n} ${unit}` });
  }
  const at = r.time ? ` · ${r.time}` : "";
  if (r.mode === "weekdays") {
    const days = (r.weekdays || []).slice().sort((a, b) => a - b).map((d) => t(DAY_KEYS[d])).join(", ");
    return (days ? `${t("tasks.recur_weekdays")}: ${days}` : t("tasks.recur_weekdays")) + at;
  }
  return t(`tasks.recur_${r.mode}`) + at;
}

/** Filter key for a CCP's monitoring frequency (mirrors backend _ccp_frequency_key). */
const frequencyKey = (c) => {
  const r = normalizeReminder(c);
  return r.enabled ? r.mode : "off";
};
const FREQUENCY_FILTERS = ["daily", "working_days", "weekdays", "weekly", "monthly", "custom", "none", "off"];
const frequencyFilterLabel = (t, key) => (key === "off" ? t("haccp.freqOff") : t(`tasks.recur_${key}`));

const splitList = (s) =>
  (s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

// Mirror of backend per-field evaluation
function fieldStatus(field, value) {
  if (value === undefined || value === null || value === "") return null;
  if (NUMERIC_TYPES.includes(field.type)) {
    const n = Number(value);
    if (Number.isNaN(n)) return null;
    if ((field.critical_min != null && n < field.critical_min) || (field.critical_max != null && n > field.critical_max))
      return "critical";
    if ((field.warning_min != null && n < field.warning_min) || (field.warning_max != null && n > field.warning_max))
      return "warn";
    return "ok";
  }
  if (field.type === "boolean") {
    const okb = field.ok_boolean ?? true;
    return Boolean(value) === Boolean(okb) ? "ok" : "critical";
  }
  if (field.type === "select") {
    if (field.ok_options && field.ok_options.length) return field.ok_options.includes(value) ? "ok" : "critical";
    return null;
  }
  return null;
}

const chipCls = (st) =>
  st === "critical" ? "badge-bad" : st === "warn" ? "badge-warn" : st === "ok" ? "badge-ok" : "bg-secondary text-muted-foreground";

function logValueText(l, t) {
  const yes = t ? t("common.yes") : "Yes";
  const no = t ? t("common.no") : "No";
  if (l.fields_snapshot) {
    const fv = l.field_values || {};
    const parts = l.fields_snapshot
      .filter((f) => f.type !== "photo")
      .map((f) => {
        let v = fv[f.id];
        if (v === undefined || v === "" || v === null) return null;
        if (typeof v === "boolean") v = v ? yes : no;
        else if (Array.isArray(v)) {
          if (!v.length) return null;
          v = v.join(", ");
        } else v = fmtFieldValue(f, v);
        return `${f.name}: ${v}${f.unit ? " " + f.unit : ""}`;
      })
      .filter(Boolean);
    return parts.join("; ") || "-";
  }
  if (l.value === null || l.value === undefined || l.value === "") return "-";
  return `${fmt(l.value, 2)} ${l.unit || ""}`.trim();
}

// Authenticated image thumbnail (img cannot send Authorization header -> fetch as blob)
function PhotoThumb({ path, className = "h-10 w-10" }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true;
    let made = null;
    fetchFileBlobUrl(path)
      .then((u) => {
        if (active) {
          made = u;
          setUrl(u);
        } else {
          URL.revokeObjectURL(u);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [path]);
  if (!url) return <div className={`${className} animate-pulse rounded bg-secondary`} />;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="haccp-photo-thumb">
      <img src={url} alt="evidence" className={`${className} rounded border border-border object-cover`} />
    </a>
  );
}

export default function Haccp() {
  const { can } = useAuth();
  const { t, lang } = useT();
  const canEdit = can("admin", "manager", "qa");
  const [ccps, setCcps] = useState([]);
  const [logs, setLogs] = useState([]);
  const [ccpDialog, setCcpDialog] = useState(null);
  const [logDialog, setLogDialog] = useState(false);
  const [caDialog, setCaDialog] = useState(null);
  const [reportDialog, setReportDialog] = useState(null); // null | { ccpId: "all" | id }
  const [operatorNames, setOperatorNames] = useState({});
  useEffect(() => {
    getOperators({}).then((ops) => setOperatorNames(Object.fromEntries((ops || []).map((o) => [o.id, o.name])))).catch(() => {});
  }, []);
  const [delLog, setDelLog] = useState(null);
  const [editLog, setEditLog] = useState(null);
  const [deepLinkCcpId, setDeepLinkCcpId] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const doResolve = async (log) => {
    try {
      await resolveHaccpLog(log.id);
      toast.success(t("haccp.logResolved"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("haccp.failedResolve")); }
  };

  const removeLog = async () => {
    if (!delLog) return;
    try {
      await deleteHaccpLog(delLog.id);
      toast.success(t("haccp.logDeleted"));
      setDelLog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("haccp.failedDeleteLog")); }
  };

  // Log filters
  const [ccpFilter, setCcpFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [freqFilter, setFreqFilter] = useState("all"); // monitoring frequency (both tabs)
  const [operatorFilter, setOperatorFilter] = useState("all"); // who logged the reading (shift review)

  const load = async () => {
    const [c, l] = await Promise.all([getCCPs(), getHaccpLogs()]);
    setCcps(c);
    setLogs(l);
  };
  useEffect(() => {
    load();
  }, []);

  // Quick action deep link (?log=1) — opens "Record reading" without a preselected CCP.
  useEffect(() => {
    if (searchParams.get("log") === "1") {
      setDeepLinkCcpId("");
      setLogDialog(true);
      const next = new URLSearchParams(searchParams); next.delete("log");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Deep link: when arriving from a reminder notification (/haccp?ccp={id}),
  // pre-select that control point and open the Add Log form to register it.
  useEffect(() => {
    const ccpParam = searchParams.get("ccp");
    if (!ccpParam || ccps.length === 0) return;
    const match = ccps.find((c) => c.id === ccpParam);
    if (match) {
      setDeepLinkCcpId(match.id);
      setLogDialog(true);
      // Clear the query param so refreshes / re-renders don't reopen the dialog.
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("ccp");
      setSearchParams(newParams, { replace: true });
    }
  }, [ccps, searchParams, setSearchParams]);

  const logFiltersActive = ccpFilter !== "all" || monthFilter !== "" || dateFilter !== "" || freqFilter !== "all" || operatorFilter !== "all";
  const clearLogFilters = () => {
    setCcpFilter("all");
    setMonthFilter("");
    setDateFilter("");
    setFreqFilter("all");
    setOperatorFilter("all");
  };
  // Operators seen on readings (so historical / renamed names still filter) + active operators.
  const operatorOptions = useMemo(() => {
    const names = new Set(logs.map((l) => (l.operator_name || "").trim()).filter(Boolean));
    Object.values(operatorNames).forEach((n) => n && names.add(n));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [logs, operatorNames]);
  const ccpFreqMap = useMemo(() => Object.fromEntries(ccps.map((c) => [c.id, frequencyKey(c)])), [ccps]);
  const filteredCcps = useMemo(() => (freqFilter === "all" ? ccps : ccps.filter((c) => ccpFreqMap[c.id] === freqFilter)), [ccps, freqFilter, ccpFreqMap]);
  // Query params mirroring the active monitoring filters (used by CSV + audit pack PDF).
  const exportParams = useMemo(() => {
    const p = {};
    if (ccpFilter !== "all") p.ccp_id = ccpFilter;
    if (dateFilter) p.date = dateFilter;
    else if (monthFilter) p.month = monthFilter;
    if (freqFilter !== "all") p.frequency = freqFilter;
    if (operatorFilter !== "all") p.operator = operatorFilter;
    return p;
  }, [ccpFilter, monthFilter, dateFilter, freqFilter, operatorFilter]);
  const filteredLogs = useMemo(() => {
    return logs.filter((l) => {
      if (ccpFilter !== "all" && l.ccp_id !== ccpFilter) return false;
      if (freqFilter !== "all" && ccpFreqMap[l.ccp_id] !== freqFilter) return false;
      if (operatorFilter !== "all" && (l.operator_name || "").trim() !== operatorFilter) return false;
      const day = (l.timestamp || "").slice(0, 10);
      if (dateFilter && day !== dateFilter) return false;
      if (monthFilter && day.slice(0, 7) !== monthFilter) return false;
      return true;
    });
  }, [logs, ccpFilter, monthFilter, dateFilter, freqFilter, ccpFreqMap, operatorFilter]);

  const logsPager = usePagination(filteredLogs, 50);

  const openAlerts = useMemo(() => logs.filter((l) => l.status === "critical" && !l.resolved), [logs]);

  const exportCsv = async () => {
    try {
      await downloadHaccpCsv(exportParams);
    } catch {
      toast.error(t("haccp.exportFailed"));
    }
  };

  const printLog = async (l) => {
    try {
      const safe = (l.ccp_name || "ccp").replace(/[^A-Za-z0-9_-]+/g, "_");
      await downloadPdf(`/haccp/logs/${l.id}/report?lang=${lang || "ro"}`, `ccp_record_${safe}_${String(l.timestamp || "").slice(0, 16).replace(/:/g, "").replace("T", "_")}.pdf`);
    } catch {
      toast.error(t("prod.reportFailed"));
    }
  };

  return (
    <Layout
      actions={
        <>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="haccp-export-button">
            <Download className="mr-1.5 h-4 w-4" /> CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const qs = new URLSearchParams({ lang: lang || "ro", ...exportParams }).toString();
              const suffix = [exportParams.date || exportParams.month, exportParams.ccp_id ? (ccps.find((c) => c.id === exportParams.ccp_id)?.name || "").replace(/[^A-Za-z0-9_-]+/g, "_") : "", exportParams.frequency, exportParams.operator ? exportParams.operator.replace(/[^A-Za-z0-9_-]+/g, "_") : ""].filter(Boolean).join("_");
              downloadPdf(`/haccp/report?${qs}`, `haccp_audit_pack${suffix ? "_" + suffix : ""}.pdf`).catch(() => toast.error(t("prod.reportFailed")));
            }}
            title={logFiltersActive ? t("haccp.pdfFiltered") : t("haccp.pdfAll")}
            data-testid="haccp-report-button"
          >
            <FileText className="mr-1.5 h-4 w-4" /> PDF{logFiltersActive ? ` · ${t("haccp.filtered")}` : ""}
          </Button>
          <Button size="sm" onClick={() => setReportDialog({ ccpId: "all" })} data-testid="haccp-ccp-report-button">
            <Printer className="mr-1.5 h-4 w-4" /> {t("haccp.ccpReport")}
          </Button>
        </>
      }
    >
      {openAlerts.length > 0 && (
        <div className="mb-5 flex items-start gap-3 rounded-xl badge-bad p-4" data-testid="haccp-alert-banner">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <div className="font-semibold">{t("haccp.unresolvedBreaches", { n: openAlerts.length })}</div>
            <div className="line-clamp-3 text-sm opacity-90 sm:line-clamp-none">
              {openAlerts.slice(0, 3).map((a) => `${a.ccp_name} (${logValueText(a, t)})`).join(", ")}
              {openAlerts.length > 3 ? "…" : ""}{t("haccp.addCorrectiveHint")}
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="logs">
        <TabsList>
          <TabsTrigger value="logs" data-testid="haccp-tab-logs">{t("haccp.tabLogs")}</TabsTrigger>
          <TabsTrigger value="ccps" data-testid="haccp-tab-ccps">{t("haccp.tabCcps")}</TabsTrigger>
          <TabsTrigger value="trends" data-testid="haccp-tab-trends">{t("haccp.tabTrends")}</TabsTrigger>
        </TabsList>

        {/* Monitoring Logs */}
        <TabsContent value="logs" className="pt-4">
          <Card>
            <CardHeader className="space-y-4">
              <div className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">
                  {t("haccp.logsTitle")} ({filteredLogs.length}{logFiltersActive ? ` ${t("prod.ofTotal", { n: logs.length })}` : ""})
                </CardTitle>
                <Button size="sm" onClick={() => setLogDialog(true)} data-testid="haccp-log-create-button">
                  <Plus className="mr-1.5 h-4 w-4" /> {t("haccp.logReading")}
                </Button>
              </div>
              <ResultsFilter
                selectLabel={t("haccp.controlPoint")}
                selectValue={ccpFilter}
                onSelectChange={setCcpFilter}
                selectOptions={ccps.map((c) => ({ value: c.id, label: c.name }))}
                allLabel={t("haccp.allCcps")}
                selectTestId="filter-ccp-select"
                selectWidth="sm:w-[220px]"
                monthValue={monthFilter}
                onMonthChange={setMonthFilter}
                monthTestId="filter-month-input"
                dateValue={dateFilter}
                onDateChange={setDateFilter}
                dateTestId="filter-date-input"
                active={logFiltersActive}
                onClear={clearLogFilters}
                clearTestId="filter-clear-button"
              >
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">{t("prod.operator")}</Label>
                  <Select value={operatorFilter} onValueChange={setOperatorFilter}>
                    <SelectTrigger className="w-[190px]" data-testid="filter-operator-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("haccp.allOperators")}</SelectItem>
                      {operatorOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">{t("haccp.frequency")}</Label>
                  <Select value={freqFilter} onValueChange={setFreqFilter}>
                    <SelectTrigger className="w-[200px]" data-testid="filter-frequency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("haccp.freqAll")}</SelectItem>
                      {FREQUENCY_FILTERS.map((k) => <SelectItem key={k} value={k}>{frequencyFilterLabel(t, k)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </ResultsFilter>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="flex flex-col items-center py-12 text-center">
                  <ClipboardCheck className="h-10 w-10 text-muted-foreground/50" />
                  <p className="mt-3 text-sm text-muted-foreground">{t("haccp.noLogs")}</p>
                </div>
              ) : filteredLogs.length === 0 ? (
                <div className="flex flex-col items-center py-12 text-center" data-testid="filter-empty-state">
                  <Filter className="h-10 w-10 text-muted-foreground/50" />
                  <p className="mt-3 text-sm text-muted-foreground">{t("haccp.noMatch")}</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={clearLogFilters} data-testid="filter-empty-clear">
                    {t("prod.clearFilters")}
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto thin-scroll">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("haccp.time")}</TableHead>
                        <TableHead>{t("haccp.ccp")}</TableHead>
                        <TableHead>{t("haccp.measurements")}</TableHead>
                        <TableHead>{t("prod.operator")}</TableHead>
                        <TableHead>{t("common.status")}</TableHead>
                        <TableHead>{t("haccp.action")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logsPager.pageItems.map((l) => (
                        <TableRow key={l.id} data-testid={`haccp-log-row-${l.id}`}>
                          <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                          {formatDateTime(l.timestamp)}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{l.ccp_name}</div>
                            <div className="text-xs text-muted-foreground">{l.process_step}</div>
                          </TableCell>
                          <TableCell>
                            <LogMeasurements log={l} />
                          </TableCell>
                          <TableCell className="text-muted-foreground">{l.operator_name || "-"}</TableCell>
                          <TableCell>
                            <StatusBadge
                              status={statusMap[l.status]}
                              label={l.status === "critical" ? t("haccp.ccpBreach") : l.status === "warn" ? t("haccp.warning") : t("haccp.inRange")}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              {l.status === "critical" && !l.corrective_action && canEdit && (
                                <Button variant="outline" size="sm" onClick={() => setCaDialog(l)} data-testid={`haccp-ca-button-${l.id}`}>
                                  {t("haccp.correctiveAction")}
                                </Button>
                              )}
                              {l.status === "critical" && !l.resolved && canEdit && (
                                <Button variant="outline" size="sm" onClick={() => doResolve(l)} data-testid={`haccp-log-resolve-${l.id}`}>
                                  {t("haccp.resolveLog")}
                                </Button>
                              )}
                              {l.resolved && l.status === "critical" && <Badge className="badge-ok">{t("haccp.resolved")}</Badge>}
                              <Button
                                variant="ghost"
                                size="icon"
                                title={t("haccp.printRecord")}
                                aria-label={t("haccp.printRecord")}
                                onClick={() => printLog(l)}
                                data-testid={`haccp-log-print-${l.id}`}
                              >
                                <Printer className="h-4 w-4 text-muted-foreground" />
                              </Button>
                              {canEdit && (
                                <Button variant="ghost" size="icon" title={t("haccp.editLog")} onClick={() => setEditLog(l)} data-testid={`haccp-log-edit-${l.id}`}>
                                  <Pencil className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              )}
                              {canEdit && (
                                <Button variant="ghost" size="icon" title={t("haccp.deleteLogTitleAttr")} onClick={() => setDelLog(l)} data-testid={`haccp-log-delete-${l.id}`}>
                                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    page={logsPager.page} pageCount={logsPager.pageCount} pageSize={logsPager.pageSize}
                    total={logsPager.total} start={logsPager.start} end={logsPager.end}
                    onPageChange={logsPager.setPage} onPageSizeChange={logsPager.setPageSize}
                    testid="haccp-logs-pagination"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CCP Definitions */}
        <TabsContent value="ccps" className="pt-4">
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-base">{t("haccp.ccpsTitle")} ({filteredCcps.length}{freqFilter !== "all" ? ` / ${ccps.length}` : ""})</CardTitle>
              <div className="flex items-center gap-2">
                <Select value={freqFilter} onValueChange={setFreqFilter}>
                  <SelectTrigger className="w-[200px]" data-testid="ccp-frequency-filter"><Filter className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" /><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("haccp.freqAll")}</SelectItem>
                    {FREQUENCY_FILTERS.map((k) => <SelectItem key={k} value={k}>{frequencyFilterLabel(t, k)}</SelectItem>)}
                  </SelectContent>
                </Select>
                {canEdit && (
                  <Button size="sm" onClick={() => setCcpDialog({ ...emptyCCP })} data-testid="haccp-ccp-create-button">
                    <Plus className="mr-1.5 h-4 w-4" /> {t("haccp.newCcp")}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {filteredCcps.length === 0 ? (
                <div className="flex flex-col items-center py-12 text-center">
                  <ShieldCheck className="h-10 w-10 text-muted-foreground/50" />
                  <p className="mt-3 text-sm text-muted-foreground">{t("haccp.noCcps")}</p>
                </div>
              ) : (
                <div className="overflow-x-auto thin-scroll">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("haccp.ccp")}</TableHead>
                        <TableHead>{t("haccp.processStep")}</TableHead>
                        <TableHead>{t("prod.type")}</TableHead>
                        <TableHead className="text-right">{t("haccp.criticalLimits")}</TableHead>
                        <TableHead>{t("haccp.frequency")}</TableHead>
                        <TableHead>{t("haccp.schedule")}</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCcps.map((c) => {
                        const custom = (c.fields?.length || 0) > 0;
                        return (
                          <TableRow key={c.id} data-testid={`haccp-ccp-row-${c.id}`}>
                            <TableCell className="font-medium">{c.name}</TableCell>
                            <TableCell className="text-muted-foreground">{c.process_step}</TableCell>
                            <TableCell>
                              {custom ? (
                                <Badge variant="secondary" className="gap-1">
                                  <ListChecks className="h-3 w-3" /> {t("haccp.customFields", { n: c.fields.length })}
                                </Badge>
                              ) : (
                                <Badge variant="secondary">
                                  {c.measurement_type}
                                  {c.unit ? ` · ${c.unit}` : ""}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {custom ? (
                                <span className="text-xs text-muted-foreground">{t("haccp.perField")}</span>
                              ) : (
                                <>
                                  {c.critical_min != null ? c.critical_min : "–"} –{" "}
                                  {c.critical_max != null ? c.critical_max : "–"}
                                </>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              <div>{c.frequency}</div>
                              {reminderLabel(t, c) && (
                                <span className="inline-flex items-center gap-1 text-xs" data-testid={`haccp-ccp-schedule-${c.id}`}>
                                  <BellRing className="h-3 w-3" /> {reminderLabel(t, c)}
                                </span>
                              )}
                              {reminderLabel(t, c) && c.task_operator_id && (
                                <span className="block text-xs" data-testid={`haccp-ccp-task-operator-${c.id}`}>
                                  <UserRound className="mr-1 inline h-3 w-3" />{operatorNames[c.task_operator_id] || t("haccp.autoTaskShort")}
                                </span>
                              )}
                              {reminderLabel(t, c) && c.escalation_minutes > 0 && (
                                <span className="block text-xs" data-testid={`haccp-ccp-escalation-${c.id}`}>
                                  <ShieldAlert className="mr-1 inline h-3 w-3 text-[hsl(var(--danger))]" />
                                  {c.escalation_minutes < 60 ? t("haccp.escalationAfterMin", { n: c.escalation_minutes }) : t("haccp.escalationAfterHours", { n: c.escalation_minutes / 60 })}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {(c.reminder_enabled ?? !!c.frequency_hours) ? (
                                c.due_status === "overdue" ? (
                                  <Badge className="badge-bad gap-1"><Clock className="h-3 w-3" /> {normalizeReminder(c).mode === "none" ? t("haccp.toComplete") : t("haccp.overdue")}</Badge>
                                ) : (
                                  <Badge className="badge-ok gap-1"><Clock className="h-3 w-3" /> {normalizeReminder(c).mode === "none" ? t("haccp.scheduled") : t("haccp.onTrack")}</Badge>
                                )
                              ) : (
                                <span className="text-xs text-muted-foreground">–</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setReportDialog({ ccpId: c.id })}
                                title={t("haccp.ccpReport")}
                                aria-label={t("haccp.ccpReport")}
                                data-testid={`haccp-ccp-print-${c.id}`}
                              >
                                <Printer className="h-4 w-4" />
                              </Button>
                              {canEdit ? (
                                <>
                                  <Button variant="ghost" size="icon" onClick={() => setCcpDialog(c)} data-testid={`haccp-ccp-edit-${c.id}`}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={async () => {
                                      await deleteCCP(c.id);
                                      toast.success(t("haccp.ccpDeleted"));
                                      load();
                                    }}
                                    data-testid={`haccp-ccp-delete-${c.id}`}
                                  >
                                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                                  </Button>
                                </>
                              ) : (
                                <span className="text-xs text-muted-foreground">{t("haccp.viewOnly")}</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trends */}
        <TabsContent value="trends" className="pt-4">
          <CCPTrends ccps={ccps} />
        </TabsContent>
      </Tabs>

      <CCPDialog ccp={ccpDialog} onClose={() => setCcpDialog(null)} onSaved={() => { setCcpDialog(null); load(); }} />
      <LogDialog open={logDialog} ccps={ccps} initialCcpId={deepLinkCcpId} onClose={() => { setLogDialog(false); setDeepLinkCcpId(""); }} onSaved={() => { setLogDialog(false); setDeepLinkCcpId(""); load(); }} />
      <CADialog log={caDialog} onClose={() => setCaDialog(null)} onSaved={() => { setCaDialog(null); load(); }} />
      <CcpReportDialog req={reportDialog} ccps={ccps} onClose={() => setReportDialog(null)} />
      <EditLogDialog log={editLog} onClose={() => setEditLog(null)} onSaved={() => { setEditLog(null); load(); }} />

      <AlertDialog open={!!delLog} onOpenChange={(o) => !o && setDelLog(null)}>
        <AlertDialogContent data-testid="haccp-log-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("haccp.deleteLogTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {delLog ? t("haccp.deleteLogDesc", { ccp: delLog.ccp_name || t("haccp.ccp"), time: delLog.timestamp ? formatDateTime(delLog.timestamp) : "" }) : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="haccp-log-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={removeLog} className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid="haccp-log-delete-confirm">{t("haccp.deleteLogBtn")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function LogMeasurements({ log }) {
  const { t } = useT();
  if (log.fields_snapshot) {
    const fv = log.field_values || {};
    const fs = log.field_statuses || {};
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {log.fields_snapshot.map((f) => {
          const v = fv[f.id];
          if (f.type === "photo") {
            return v ? <PhotoThumb key={f.id} path={v} /> : null;
          }
          if (v === undefined || v === "" || v === null) return null;
          if (Array.isArray(v) && v.length === 0) return null;
          const disp = typeof v === "boolean" ? (v ? t("common.yes") : t("common.no")) : Array.isArray(v) ? v.join(", ") : fmtFieldValue(f, v);
          return (
            <span key={f.id} className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs ${chipCls(fs[f.id])}`}>
              <span className="opacity-70">{f.name}:</span>
              <span className="font-medium">
                {disp}
                {f.unit ? ` ${f.unit}` : ""}
              </span>
            </span>
          );
        })}
      </div>
    );
  }
  return (
    <span className="font-mono tabular-nums font-semibold">
      {fmt(log.value, 2)} {log.unit}
    </span>
  );
}

// ---------------- CCP Definition dialog (single OR custom multi-field) ----------------
function toEditField(x) {
  return {
    id: x.id || uid(),
    name: x.name || "",
    type: x.type || "number",
    unit: x.unit || "",
    required: !!x.required,
    critical_min: x.critical_min ?? "",
    critical_max: x.critical_max ?? "",
    warning_min: x.warning_min ?? "",
    warning_max: x.warning_max ?? "",
    optionsStr: (x.options || []).join(", "),
    okOptionsStr: (x.ok_options || []).join(", "),
    ok_boolean: x.ok_boolean ?? true,
  };
}

function CCPDialog({ ccp, onClose, onSaved }) {
  const { t } = useT();
  const [f, setF] = useState(null);
  const [operators, setOperators] = useState([]);
  useEffect(() => {
    if (!ccp) return;
    getOperators({ active: true }).then((ops) => setOperators(ops || [])).catch((e) => console.error("HACCP: failed to load operators", e));
  }, [ccp]);
  useEffect(() => {
    (async () => {
      if (!ccp) {
        setF(null);
        return;
      }
      setF({
        ...emptyCCP,
        ...ccp,
        critical_min: ccp.critical_min ?? "",
        critical_max: ccp.critical_max ?? "",
        warning_min: ccp.warning_min ?? "",
        warning_max: ccp.warning_max ?? "",
        frequency_hours: ccp.frequency_hours ?? "",
        frequency_weeks: ccp.frequency_weeks ?? "",
        ...(() => {
          const r = normalizeReminder(ccp);
          return {
            reminder_enabled: ccp.id ? r.enabled : false,
            reminder_mode: ccp.id ? r.mode : "daily",
            recurrence_interval: r.interval,
            recurrence_unit: r.unit,
            recurrence_weekdays: r.weekdays,
            reminder_time: r.time || "",
            task_operator_id: r.task_operator_id || "",
            escalation_minutes: r.escalation_minutes || "",
            due_at: r.due_at ? String(r.due_at).slice(0, 16) : "",
          };
        })(),
        fields: (ccp.fields || []).map(toEditField),
        __custom: (ccp.fields?.length || 0) > 0,
      });
    })();
  }, [ccp]);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const addField = () =>
    setF((x) => ({ ...x, fields: [...(x.fields || []), toEditField({ name: "", type: "number", unit: "" })] }));
  const updateField = (id, patch) =>
    setF((x) => ({ ...x, fields: x.fields.map((fl) => (fl.id === id ? { ...fl, ...patch } : fl)) }));
  const removeField = (id) => setF((x) => ({ ...x, fields: x.fields.filter((fl) => fl.id !== id) }));

  const save = async () => {
    if (!f.name.trim() || !f.process_step.trim()) {
      toast.error(t("haccp.nameStepRequired"));
      return;
    }
    let fields = [];
    if (f.__custom) {
      const named = (f.fields || []).filter((x) => x.name.trim());
      if (named.length === 0) {
        toast.error(t("haccp.addOneField"));
        return;
      }
      fields = named.map((x) => ({
        id: x.id,
        name: x.name.trim(),
        type: x.type,
        unit: x.unit || "",
        required: !!x.required,
        critical_min: NUMERIC_TYPES.includes(x.type) ? numOrNull(x.critical_min) : null,
        critical_max: NUMERIC_TYPES.includes(x.type) ? numOrNull(x.critical_max) : null,
        warning_min: NUMERIC_TYPES.includes(x.type) ? numOrNull(x.warning_min) : null,
        warning_max: NUMERIC_TYPES.includes(x.type) ? numOrNull(x.warning_max) : null,
        options: (x.type === "select" || x.type === "checklist") ? splitList(x.optionsStr) : null,
        ok_options: x.type === "select" ? splitList(x.okOptionsStr) : null,
        ok_boolean: x.type === "boolean" ? !!x.ok_boolean : true,
      }));
    }
    const payload = {
      name: f.name,
      process_step: f.process_step,
      measurement_type: f.measurement_type,
      unit: f.__custom ? "" : f.unit,
      critical_min: f.__custom ? null : numOrNull(f.critical_min),
      critical_max: f.__custom ? null : numOrNull(f.critical_max),
      warning_min: f.__custom ? null : numOrNull(f.warning_min),
      warning_max: f.__custom ? null : numOrNull(f.warning_max),
      frequency: f.frequency,
      reminder_enabled: !!f.reminder_enabled,
      reminder_mode: f.reminder_mode || "daily",
      recurrence_interval: f.reminder_mode === "custom" ? Math.max(parseInt(f.recurrence_interval, 10) || 1, 1) : null,
      recurrence_unit: f.reminder_mode === "custom" ? (f.recurrence_unit || "hours") : null,
      recurrence_weekdays: f.reminder_mode === "weekdays" ? (f.recurrence_weekdays || []) : null,
      reminder_time: DAY_MODES.includes(f.reminder_mode) && f.reminder_time ? f.reminder_time : null,
      task_operator_id: f.reminder_enabled && f.task_operator_id ? f.task_operator_id : null,
      escalation_minutes: f.reminder_enabled && f.escalation_minutes ? parseInt(f.escalation_minutes, 10) || null : null,
      due_at: f.reminder_enabled && f.due_at ? f.due_at : null,
      notes: f.notes,
      fields,
    };
    if (f.reminder_enabled && f.reminder_mode === "weekdays" && !(f.recurrence_weekdays || []).length) {
      toast.error(t("tasks.pickDays"));
      return;
    }
    if (f.reminder_enabled && f.reminder_mode === "none" && !f.due_at) {
      toast.error(t("haccp.dueRequired"));
      return;
    }
    try {
      if (f.id) await updateCCP(f.id, payload);
      else await createCCP(payload);
      toast.success(t("haccp.ccpSaved"));
      onSaved();
    } catch {
      toast.error(t("haccp.failedSaveCcp"));
    }
  };

  return (
    <Dialog open={!!ccp} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto thin-scroll">
        <DialogHeader>
          <DialogTitle>{f.id ? t("haccp.editCcp") : t("haccp.newControlPoint")}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t("haccp.ccpName")}</Label>
            <Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="ccp-name-input" placeholder={t("haccp.ccpNamePlaceholder")} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("haccp.processStep")}</Label>
            <Input value={f.process_step} onChange={(e) => set("process_step", e.target.value)} data-testid="ccp-step-input" placeholder={t("haccp.stepPlaceholder")} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("haccp.frequency")}</Label>
            <Input value={f.frequency} onChange={(e) => set("frequency", e.target.value)} placeholder={t("haccp.freqPlaceholder")} data-testid="ccp-frequency-input" />
          </div>
          <div className="sm:col-span-2 space-y-3 rounded-lg border border-border p-3" data-testid="ccp-reminder-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BellRing className="h-4 w-4 text-[hsl(var(--brand))]" />
                <div>
                  <div className="text-sm font-medium">{t("haccp.reminderTitle")}</div>
                  <div className="text-xs text-muted-foreground">{t("haccp.reminderHint")}</div>
                </div>
              </div>
              <Switch checked={!!f.reminder_enabled} onCheckedChange={(v) => set("reminder_enabled", v)} data-testid="ccp-reminder-toggle" />
            </div>
            {f.reminder_enabled && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("tasks.repeat")}</Label>
                    <Select value={f.reminder_mode} onValueChange={(v) => set("reminder_mode", v)}>
                      <SelectTrigger data-testid="ccp-reminder-mode-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("tasks.recur_none")}</SelectItem>
                        <SelectItem value="daily">{t("tasks.recur_daily")}</SelectItem>
                        <SelectItem value="working_days">{t("tasks.recur_working_days")}</SelectItem>
                        <SelectItem value="weekdays">{t("tasks.recur_weekdays")}</SelectItem>
                        <SelectItem value="weekly">{t("tasks.recur_weekly")}</SelectItem>
                        <SelectItem value="monthly">{t("tasks.recur_monthly")}</SelectItem>
                        <SelectItem value="custom">{t("tasks.recur_custom")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">{f.reminder_mode === "none" ? t("haccp.dueDateTime") : t("haccp.firstDueOpt")}</Label>
                    <Input type="datetime-local" value={f.due_at ?? ""} onChange={(e) => set("due_at", e.target.value)} className="font-mono" data-testid="ccp-due-at-input" />
                  </div>
                </div>
                {f.reminder_mode === "weekdays" && (
                  <div className="flex flex-wrap gap-1.5" data-testid="ccp-weekday-picker">
                    {WEEKDAYS.map((d) => {
                      const active = (f.recurrence_weekdays || []).includes(d);
                      return (
                        <button
                          key={d}
                          type="button"
                          data-testid={`ccp-weekday-${d}`}
                          aria-pressed={active}
                          onClick={() => {
                            const cur = new Set(f.recurrence_weekdays || []);
                            active ? cur.delete(d) : cur.add(d);
                            set("recurrence_weekdays", [...cur].sort((a, b) => a - b));
                          }}
                          className={`h-9 w-11 rounded-lg border text-xs font-medium transition-colors ${active
                            ? "border-transparent bg-[hsl(var(--brand))] text-white"
                            : "border-border bg-card text-muted-foreground hover:bg-secondary"}`}
                        >
                          {t(DAY_KEYS[d])}
                        </button>
                      );
                    })}
                  </div>
                )}
                {f.reminder_mode === "custom" && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{t("tasks.every")}</span>
                    <Input type="number" min="1" step="1" value={f.recurrence_interval ?? 1}
                      onChange={(e) => set("recurrence_interval", e.target.value)}
                      className="w-20 text-right font-mono" data-testid="ccp-recurrence-interval" />
                    <Select value={f.recurrence_unit || "hours"} onValueChange={(v) => set("recurrence_unit", v)}>
                      <SelectTrigger className="w-32" data-testid="ccp-recurrence-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hours">{t("haccp.unitHoursPlain")}</SelectItem>
                        <SelectItem value="days">{t("tasks.unitDaysPlain")}</SelectItem>
                        <SelectItem value="weeks">{t("tasks.unitWeeksPlain")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {DAY_MODES.includes(f.reminder_mode) && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-xs">{t("haccp.reminderTime")}</Label>
                    <Input type="time" value={f.reminder_time || ""} onChange={(e) => set("reminder_time", e.target.value)} className="w-32 font-mono" data-testid="ccp-reminder-time-input" />
                    <span className="text-[11px] text-muted-foreground">{t("haccp.reminderTimeHint")}</span>
                  </div>
                )}
                <div className="space-y-1.5 rounded-md border border-dashed border-border p-2.5">
                  <Label className="flex items-center gap-1.5 text-xs"><UserRound className="h-3.5 w-3.5 text-[hsl(var(--brand))]" /> {t("haccp.autoTaskOperator")}</Label>
                  <Select value={f.task_operator_id || "__none__"} onValueChange={(v) => set("task_operator_id", v === "__none__" ? "" : v)}>
                    <SelectTrigger data-testid="ccp-task-operator-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t("haccp.autoTaskNone")}</SelectItem>
                      {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">{t("haccp.autoTaskHint")}</p>
                </div>
                <div className="space-y-1.5 rounded-md border border-dashed border-border p-2.5">
                  <Label className="flex items-center gap-1.5 text-xs"><ShieldAlert className="h-3.5 w-3.5 text-[hsl(var(--danger))]" /> {t("haccp.escalation")}</Label>
                  <Select value={f.escalation_minutes ? String(f.escalation_minutes) : "__off__"} onValueChange={(v) => set("escalation_minutes", v === "__off__" ? "" : v)}>
                    <SelectTrigger data-testid="ccp-escalation-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__off__">{t("haccp.escalationOff")}</SelectItem>
                      {ESCALATION_OPTIONS.map((m) => (
                        <SelectItem key={m} value={String(m)}>{m < 60 ? t("haccp.escalationAfterMin", { n: m }) : t("haccp.escalationAfterHours", { n: m / 60 })}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">{t("haccp.escalationHint")}</p>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {f.reminder_mode === "none" ? t("haccp.reminderOnceHint") : t("haccp.reminderRepeatHint")}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Monitoring mode toggle */}
        <div className="mt-2 flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
          <div>
            <div className="text-sm font-medium">{t("haccp.customTitle")}</div>
            <div className="text-xs text-muted-foreground">{t("haccp.customHint")}</div>
          </div>
          <Switch checked={!!f.__custom} onCheckedChange={(v) => set("__custom", v)} data-testid="ccp-custom-toggle" />
        </div>

        {!f.__custom ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("haccp.measurementType")}</Label>
              <Select value={f.measurement_type} onValueChange={(v) => set("measurement_type", v)}>
                <SelectTrigger data-testid="ccp-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MEAS_TYPES.map((mt) => <SelectItem key={mt} value={mt}>{mt}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.unit")}</Label>
              <Input value={f.unit} onChange={(e) => set("unit", e.target.value)} data-testid="ccp-unit-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("haccp.critMin")}</Label>
              <Input type="number" step="0.01" value={f.critical_min} onChange={(e) => set("critical_min", e.target.value)} className="text-right font-mono" data-testid="ccp-cmin-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("haccp.critMax")}</Label>
              <Input type="number" step="0.01" value={f.critical_max} onChange={(e) => set("critical_max", e.target.value)} className="text-right font-mono" data-testid="ccp-cmax-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("haccp.warnMinOpt")}</Label>
              <Input type="number" step="0.01" value={f.warning_min} onChange={(e) => set("warning_min", e.target.value)} className="text-right font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("haccp.warnMaxOpt")}</Label>
              <Input type="number" step="0.01" value={f.warning_max} onChange={(e) => set("warning_max", e.target.value)} className="text-right font-mono" />
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="ccp-fields-builder">
            <div className="flex items-center justify-between">
              <Label className="text-sm">{t("haccp.monitoringFields")}</Label>
              <Button type="button" variant="outline" size="sm" onClick={addField} data-testid="ccp-add-field-button">
                <Plus className="mr-1.5 h-3.5 w-3.5" /> {t("prod.addField")}
              </Button>
            </div>
            {(f.fields || []).length === 0 && (
              <p className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                {t("haccp.noFieldsHint")}
              </p>
            )}
            {(f.fields || []).map((fl, idx) => (
              <CCPFieldRow key={fl.id} field={fl} index={idx} onChange={(patch) => updateField(fl.id, patch)} onRemove={() => removeField(fl.id)} />
            ))}
          </div>
        )}

        <div className="space-y-1.5">
          <Label>{t("common.notes")}</Label>
          <Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={save} data-testid="ccp-save-button">{t("haccp.saveCcp")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CCPFieldRow({ field, index, onChange, onRemove }) {
  const { t } = useT();
  const isNumeric = NUMERIC_TYPES.includes(field.type);
  return (
    <div className="space-y-3 rounded-lg border border-border p-3" data-testid={`ccp-field-row-${index}`}>
      <div className="flex items-end gap-2">
        <div className="flex-1 space-y-1.5">
          <Label className="text-xs">{t("prod.fieldName")}</Label>
          <Input value={field.name} onChange={(e) => onChange({ name: e.target.value })} placeholder={t("haccp.fieldNamePlaceholder")} data-testid={`ccp-field-name-${index}`} />
        </div>
        <div className="w-36 space-y-1.5">
          <Label className="text-xs">{t("prod.type")}</Label>
          <Select value={field.type} onValueChange={(v) => onChange({ type: v })}>
            <SelectTrigger data-testid={`ccp-field-type-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>{FIELD_TYPES.map((ft) => <SelectItem key={ft} value={ft}>{ft}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={onRemove} data-testid={`ccp-field-remove-${index}`}>
          <Trash2 className="h-4 w-4 text-muted-foreground" />
        </Button>
      </div>

      {isNumeric && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("common.unit")}</Label>
            <Input value={field.unit} onChange={(e) => onChange({ unit: e.target.value })} placeholder="°C" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("haccp.critMinShort")}</Label>
            <Input type="number" step="0.01" value={field.critical_min} onChange={(e) => onChange({ critical_min: e.target.value })} className="text-right font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("haccp.critMaxShort")}</Label>
            <Input type="number" step="0.01" value={field.critical_max} onChange={(e) => onChange({ critical_max: e.target.value })} className="text-right font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("haccp.warnMinShort")}</Label>
            <Input type="number" step="0.01" value={field.warning_min} onChange={(e) => onChange({ warning_min: e.target.value })} className="text-right font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("haccp.warnMaxShort")}</Label>
            <Input type="number" step="0.01" value={field.warning_max} onChange={(e) => onChange({ warning_max: e.target.value })} className="text-right font-mono" />
          </div>
        </div>
      )}

      {field.type === "boolean" && (
        <div className="flex items-center gap-3">
          <Switch checked={!!field.ok_boolean} onCheckedChange={(v) => onChange({ ok_boolean: v })} />
          <span className="text-xs text-muted-foreground">{t("haccp.acceptableAnswer", { ans: field.ok_boolean ? t("common.yes") : t("common.no") })}</span>
        </div>
      )}

      {field.type === "select" && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("prod.options")}</Label>
            <Input value={field.optionsStr} onChange={(e) => onChange({ optionsStr: e.target.value })} placeholder="A, B, Reject" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("haccp.acceptableOptions")}</Label>
            <Input value={field.okOptionsStr} onChange={(e) => onChange({ okOptionsStr: e.target.value })} placeholder="A, B" />
          </div>
        </div>
      )}

      {field.type === "checklist" && (
        <div className="space-y-1.5">
          <Label className="text-xs">{t("haccp.checklistItems")}</Label>
          <Input value={field.optionsStr} onChange={(e) => onChange({ optionsStr: e.target.value })} placeholder="Gloves, Hairnet, Apron" data-testid={`ccp-field-checklist-${index}`} />
          <p className="text-xs text-muted-foreground">{t("haccp.checklistHint")}</p>
        </div>
      )}

      {field.type === "text" && <p className="text-xs text-muted-foreground">{t("haccp.freeTextHint")}</p>}
      {field.type === "date" && <p className="text-xs text-muted-foreground">{t("haccp.dateFieldHint")}</p>}
      {field.type === "datetime" && <p className="text-xs text-muted-foreground">{t("haccp.datetimeFieldHint")}</p>}
      {field.type === "photo" && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Camera className="h-3.5 w-3.5" /> {t("haccp.photoHint")}
        </p>
      )}
    </div>
  );
}

// ---------------- Printable CCP monitoring report dialog ----------------
function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function CcpReportDialog({ req, ccps, onClose }) {
  const { t, lang } = useT();
  const [ccpId, setCcpId] = useState("all");
  const [month, setMonth] = useState(currentMonth());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!req) return;
    setCcpId(req.ccpId || "all");
    setMonth(currentMonth()); // default: present month
  }, [req]);

  const selected = ccps.find((c) => c.id === ccpId);

  const generate = async () => {
    if (!/^\d{4}-\d{2}$/.test(month)) {
      toast.error(t("haccp.reportInvalidMonth"));
      return;
    }
    setBusy(true);
    try {
      const qs = new URLSearchParams({ month, ccp_id: ccpId, lang: lang || "ro" }).toString();
      const safe = (selected?.name || "all").replace(/[^A-Za-z0-9_-]+/g, "_");
      await downloadPdf(`/haccp/ccp-report?${qs}`, `ccp_report_${month}_${safe}.pdf`);
      toast.success(t("haccp.reportReady"));
      onClose();
    } catch {
      toast.error(t("prod.reportFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="ccp-report-dialog">
        <DialogHeader>
          <DialogTitle>{t("haccp.ccpReport")}</DialogTitle>
          <DialogDescription>{t("haccp.ccpReportDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("haccp.controlPoint")}</Label>
            <Select value={ccpId} onValueChange={setCcpId}>
              <SelectTrigger data-testid="ccp-report-ccp-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("haccp.allCcps")}</SelectItem>
                {ccps.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("haccp.reportMonth")}</Label>
            <Input type="month" value={month} max={currentMonth()} onChange={(e) => setMonth(e.target.value)} className="font-mono" data-testid="ccp-report-month-input" />
          </div>
          <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground" data-testid="ccp-report-contents">
            <li>{t("haccp.reportIncl1")}</li>
            <li>{t("haccp.reportIncl2")}</li>
            <li>{t("haccp.reportIncl3")}</li>
            <li>{t("haccp.reportIncl4")}</li>
          </ul>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={generate} disabled={busy} data-testid="ccp-report-generate-button">
            <Printer className="mr-1.5 h-4 w-4" /> {busy ? t("haccp.reportGenerating") : t("haccp.reportGenerate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Log reading dialog ----------------
/** Current local date-time formatted for an <input type="datetime-local"> (YYYY-MM-DDTHH:mm). */
function localDateTimeNow() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function LogDialog({ open, ccps, initialCcpId, onClose, onSaved }) {
  const { t } = useT();
  const [ccpId, setCcpId] = useState("");
  const [value, setValue] = useState("");
  const [timestamp, setTimestamp] = useState(localDateTimeNow());
  const [fieldValues, setFieldValues] = useState({});
  const [operator, setOperator] = useState("");
  const [productionCode, setProductionCode] = useState("");
  const [notes, setNotes] = useState("");
  const [operators, setOperators] = useState([]);
  const [saving, setSaving] = useState(false);

  const ccp = ccps.find((c) => c.id === ccpId);
  const customFields = ccp?.fields || [];

  useEffect(() => {
    getOperators({ active: true }).then((ops) => setOperators(ops || [])).catch((e) => console.error("HACCP: failed to load operators", e));
  }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setCcpId(initialCcpId || ccps[0]?.id || "");
      setValue("");
      setTimestamp(localDateTimeNow()); // default: present day/time
      setOperator("");
      setProductionCode("");
      setNotes("");
    })();
  }, [open, ccps, initialCcpId]);

  // Initialise field values when CCP changes
  useEffect(() => {
    (async () => {
      const init = {};
      (ccp?.fields || []).forEach((fl) => {
        if (fl.type === "boolean") init[fl.id] = fl.ok_boolean ?? true;
        else if (fl.type === "checklist") init[fl.id] = [];
        else if (fl.type === "datetime") init[fl.id] = localDateTimeNow(); // default: now
        else if (fl.type === "date") init[fl.id] = localDateTimeNow().slice(0, 10); // default: today
        else init[fl.id] = "";
      });
      setFieldValues(init);
      setValue("");
    })();
  }, [ccpId]); // eslint-disable-line react-hooks/exhaustive-deps

  const previewStatus = useMemo(() => {
    if (!ccp || customFields.length || value === "") return null;
    const v = Number(value);
    if (Number.isNaN(v)) return null;
    if ((ccp.critical_min != null && v < ccp.critical_min) || (ccp.critical_max != null && v > ccp.critical_max)) return "bad";
    if ((ccp.warning_min != null && v < ccp.warning_min) || (ccp.warning_max != null && v > ccp.warning_max)) return "warn";
    return "ok";
  }, [ccp, value, customFields.length]);

  const setFV = (id, v) => setFieldValues((x) => ({ ...x, [id]: v }));

  const save = async () => {
    if (!ccpId) {
      toast.error(t("haccp.selectControlPoint"));
      return;
    }
    setSaving(true);
    try {
      let payload;
      if (customFields.length) {
        for (const fl of customFields) {
          if (fl.required) {
            const v = fieldValues[fl.id];
            const empty = v === undefined || v === "" || v === null || (Array.isArray(v) && v.length === 0);
            if (empty) {
              toast.error(t("haccp.fieldRequired", { field: fl.name }));
              setSaving(false);
              return;
            }
          }
        }
        payload = { ccp_id: ccpId, field_values: fieldValues, operator_name: operator, production_code: productionCode, notes };
      } else {
        if (value === "") {
          toast.error(t("haccp.enterReading"));
          setSaving(false);
          return;
        }
        payload = { ccp_id: ccpId, value: Number(value), operator_name: operator, production_code: productionCode, notes };
      }
      if (timestamp) {
        const ts = new Date(timestamp);
        if (Number.isNaN(ts.getTime())) {
          toast.error(t("haccp.invalidDate"));
          setSaving(false);
          return;
        }
        payload.timestamp = ts.toISOString();
      }
      const res = await createHaccpLog(payload);
      if (res.status === "critical") toast.error(t("haccp.breachRecorded"));
      else toast.success(t("haccp.readingLogged"));
      onSaved();
    } catch {
      toast.error(t("haccp.failedLog"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto thin-scroll">
        <DialogHeader>
          <DialogTitle>{t("haccp.logCcpReading")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("haccp.controlPoint")}</Label>
            <Select value={ccpId} onValueChange={setCcpId}>
              <SelectTrigger data-testid="log-ccp-select"><SelectValue placeholder={t("haccp.selectCcp")} /></SelectTrigger>
              <SelectContent>
                {ccps.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {customFields.length ? (
            <div className="space-y-3 rounded-lg border border-border p-3">
              {customFields.map((fl) => (
                <DynamicField key={fl.id} field={fl} value={fieldValues[fl.id]} onChange={(v) => setFV(fl.id, v)} />
              ))}
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>{t("haccp.readingValue")}</Label>
                {previewStatus && (
                  <StatusBadge status={previewStatus} label={previewStatus === "bad" ? t("haccp.ccpBreach") : previewStatus === "warn" ? t("haccp.warning") : t("haccp.inRange")} />
                )}
              </div>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className={`pr-12 text-right font-mono ${previewStatus === "bad" ? "border-[hsl(var(--danger))]" : ""}`}
                  data-testid="log-value-input"
                />
                {ccp?.unit && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{ccp.unit}</span>}
              </div>
              {ccp && (
                <p className="font-mono text-xs text-muted-foreground">
                  {t("haccp.criticalRange", { min: ccp.critical_min ?? "–", max: ccp.critical_max ?? "–", unit: ccp.unit || "" })}
                </p>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label>{t("haccp.readingDateTime")}</Label>
            <Input
              type="datetime-local"
              value={timestamp}
              max={localDateTimeNow()}
              onChange={(e) => setTimestamp(e.target.value)}
              className="font-mono"
              data-testid="log-timestamp-input"
            />
            <p className="text-xs text-muted-foreground">{t("haccp.readingDateHint")}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("prod.operator")}</Label>
              <Select value={operator || "__none__"} onValueChange={(v) => setOperator(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="log-operator-input">
                  <SelectValue placeholder={t("prod.selectOperator")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("prod.selectOperator")}</SelectItem>
                  {operator && !operators.some((o) => o.name === operator) && (
                    <SelectItem value={operator}>{operator}</SelectItem>
                  )}
                  {operators.map((o) => (
                    <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("haccp.productionOpt")}</Label>
              <Input value={productionCode} onChange={(e) => setProductionCode(e.target.value)} data-testid="log-production-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.notes")}</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="log-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="log-save-button">{saving ? t("common.saving") : t("haccp.saveReading")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Edit existing log dialog ----------------
function EditLogDialog({ log, onClose, onSaved }) {
  const { t } = useT();
  const [value, setValue] = useState("");
  const [fieldValues, setFieldValues] = useState({});
  const [operator, setOperator] = useState("");
  const [productionCode, setProductionCode] = useState("");
  const [notes, setNotes] = useState("");
  const [timestamp, setTimestamp] = useState("");
  const [operators, setOperators] = useState([]);
  const [saving, setSaving] = useState(false);

  const fields = log?.fields_snapshot || null;

  useEffect(() => {
    getOperators({ active: true }).then((ops) => setOperators(ops || [])).catch((e) => console.error("HACCP: failed to load operators", e));
  }, []);

  useEffect(() => {
    if (!log) return;
    (async () => {
      setValue(log.value ?? "");
      setFieldValues({ ...(log.field_values || {}) });
      setOperator(log.operator_name || "");
      setProductionCode(log.production_code || "");
      setNotes(log.notes || "");
      setTimestamp(log.timestamp ? String(log.timestamp).slice(0, 16) : "");
    })();
  }, [log]);

  const setFV = (id, v) => setFieldValues((x) => ({ ...x, [id]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        operator_name: operator,
        production_code: productionCode,
        notes,
        timestamp: timestamp ? new Date(timestamp).toISOString() : undefined,
      };
      if (fields) {
        for (const fl of fields) {
          if (fl.required) {
            const v = fieldValues[fl.id];
            const empty = v === undefined || v === "" || v === null || (Array.isArray(v) && v.length === 0);
            if (empty) { toast.error(t("haccp.fieldRequired", { field: fl.name })); setSaving(false); return; }
          }
        }
        payload.field_values = fieldValues;
      } else {
        if (value === "" || value === null) { toast.error(t("haccp.enterReading")); setSaving(false); return; }
        payload.value = Number(value);
      }
      await updateHaccpLog(log.id, payload);
      toast.success(t("haccp.logUpdated"));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("haccp.failedUpdateLog"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!log} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto thin-scroll" data-testid="haccp-edit-log-dialog">
        <DialogHeader>
          <DialogTitle>{t("haccp.editLogTitle")}</DialogTitle>
        </DialogHeader>
        {log && (
          <div className="space-y-3">
            <div className="rounded-lg border border-border px-3 py-2 text-sm">
              <div className="font-medium">{log.ccp_name}</div>
              <div className="text-xs text-muted-foreground">{log.process_step}</div>
            </div>

            {fields ? (
              <div className="space-y-3 rounded-lg border border-border p-3">
                {fields.map((fl) => (
                  <DynamicField key={fl.id} field={fl} value={fieldValues[fl.id]} onChange={(v) => setFV(fl.id, v)} />
                ))}
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>{t("haccp.readingValue")}</Label>
                <Input type="number" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} className="text-right font-mono" data-testid="edit-log-value-input" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("prod.operator")}</Label>
                <Select value={operator || "__none__"} onValueChange={(v) => setOperator(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="edit-log-operator-input"><SelectValue placeholder={t("prod.selectOperator")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("prod.selectOperator")}</SelectItem>
                    {operator && !operators.some((o) => o.name === operator) && (
                      <SelectItem value={operator}>{operator}</SelectItem>
                    )}
                    {operators.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("haccp.time")}</Label>
                <Input type="datetime-local" value={timestamp} onChange={(e) => setTimestamp(e.target.value)} className="font-mono" data-testid="edit-log-timestamp-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("haccp.productionOpt")}</Label>
              <Input value={productionCode} onChange={(e) => setProductionCode(e.target.value)} data-testid="edit-log-production-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.notes")}</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-log-notes-input" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-log-save-button">{saving ? t("common.saving") : t("haccp.saveChanges")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DynamicField({ field, value, onChange }) {
  const { t } = useT();
  const st = fieldStatus(field, value);
  return (
    <div className="space-y-1.5" data-testid={`log-field-${field.id}`}>
      <div className="flex items-center justify-between">
        <Label className="text-sm">
          {field.name}
          {field.required && <span className="text-[hsl(var(--danger))]"> *</span>}
        </Label>
        {st && (
          <StatusBadge status={statusMap[st]} label={st === "critical" ? t("haccp.outOfLimit") : st === "warn" ? t("haccp.warning") : t("haccp.inRange")} />
        )}
      </div>

      {NUMERIC_TYPES.includes(field.type) && (
        <div className="relative">
          <Input
            type="number"
            step="0.01"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className={`pr-12 text-right font-mono ${st === "critical" ? "border-[hsl(var(--danger))]" : ""}`}
            data-testid={`log-field-input-${field.id}`}
          />
          {field.unit && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{field.unit}</span>}
        </div>
      )}

      {field.type === "boolean" && (
        <div className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
          <Switch checked={!!value} onCheckedChange={(v) => onChange(v)} data-testid={`log-field-input-${field.id}`} />
          <span className="text-sm">{value ? t("common.yes") : t("common.no")}</span>
        </div>
      )}

      {field.type === "select" && (
        <Select value={value || ""} onValueChange={(v) => onChange(v)}>
          <SelectTrigger data-testid={`log-field-input-${field.id}`}><SelectValue placeholder={t("haccp.selectEllipsis")} /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {field.type === "checklist" && (
        <div className="space-y-2 rounded-md border border-border p-2.5" data-testid={`log-field-input-${field.id}`}>
          {(field.options || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">{t("haccp.noChecklistItems")}</p>
          ) : (
            (field.options || []).map((o) => {
              const arr = Array.isArray(value) ? value : [];
              const checked = arr.includes(o);
              return (
                <label key={o} className="flex cursor-pointer items-center gap-2.5 text-sm">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(c) => onChange(c ? [...arr, o] : arr.filter((i) => i !== o))}
                    data-testid={`log-field-check-${field.id}-${o}`}
                  />
                  <span>{o}</span>
                </label>
              );
            })
          )}
        </div>
      )}

      {field.type === "text" && (
        <Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`log-field-input-${field.id}`} />
      )}

      {field.type === "date" && (
        <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="font-mono" data-testid={`log-field-input-${field.id}`} />
      )}

      {field.type === "datetime" && (
        <Input type="datetime-local" value={value || ""} onChange={(e) => onChange(e.target.value)} className="font-mono" data-testid={`log-field-input-${field.id}`} />
      )}

      {field.type === "photo" && <PhotoUploadField field={field} value={value} onChange={onChange} />}

      {NUMERIC_TYPES.includes(field.type) && (field.critical_min != null || field.critical_max != null) && (
        <p className="font-mono text-xs text-muted-foreground">
          {t("haccp.criticalRange", { min: field.critical_min ?? "–", max: field.critical_max ?? "–", unit: field.unit || "" })}
        </p>
      )}
    </div>
  );
}

function PhotoUploadField({ field, value, onChange }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadFile(file);
      onChange(res.path);
      toast.success(t("haccp.photoUploaded"));
    } catch {
      toast.error(t("haccp.uploadFailed"));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-2">
      {value ? <PhotoThumb path={value} className="h-12 w-12" /> : null}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handle} data-testid={`log-photo-input-${field.id}`} />
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`log-photo-upload-${field.id}`}>
        <Upload className="mr-1.5 h-4 w-4" /> {busy ? t("haccp.uploading") : value ? t("haccp.replace") : t("haccp.uploadPhoto")}
      </Button>
      {value && (
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} data-testid={`log-photo-remove-${field.id}`}>
          {t("haccp.remove")}
        </Button>
      )}
    </div>
  );
}

// ---------------- Corrective action dialog ----------------
function CADialog({ log, onClose, onSaved }) {
  const { t } = useT();
  const [actions, setActions] = useState("");
  const [disposition, setDisposition] = useState("");
  const [verifiedBy, setVerifiedBy] = useState("");
  useEffect(() => {
    (async () => {
      setActions("");
      setDisposition("");
      setVerifiedBy("");
    })();
  }, [log]);
  if (!log) return null;
  const save = async () => {
    if (!actions.trim()) {
      toast.error(t("haccp.describeAction"));
      return;
    }
    try {
      await addCorrectiveAction(log.id, { actions, disposition, verified_by: verifiedBy });
      toast.success(t("haccp.actionRecorded"));
      onSaved();
    } catch {
      toast.error(t("settings.failedSave"));
    }
  };
  return (
    <Dialog open={!!log} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("haccp.correctiveAction")}</DialogTitle>
        </DialogHeader>
        <div className="rounded-lg badge-bad p-3 text-sm">
          <span className="font-semibold">{log.ccp_name}</span>: {logValueText(log, t)} ({t("haccp.breachWord")})
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("haccp.actionsTaken")}</Label>
            <Textarea rows={3} value={actions} onChange={(e) => setActions(e.target.value)} data-testid="ca-actions-input" placeholder={t("haccp.actionsPlaceholder")} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("haccp.disposition")}</Label>
            <Input value={disposition} onChange={(e) => setDisposition(e.target.value)} data-testid="ca-disposition-input" placeholder={t("haccp.dispositionPlaceholder")} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("haccp.verifiedBy")}</Label>
            <Input value={verifiedBy} onChange={(e) => setVerifiedBy(e.target.value)} data-testid="ca-verified-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={save} data-testid="ca-save-button">{t("haccp.closeIncident")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Trends ----------------
function CCPTrends({ ccps }) {
  const { t } = useT();
  const [ccpId, setCcpId] = useState("");
  const [fieldId, setFieldId] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ccpId || !ccps.length) return;
    (async () => { setCcpId(ccps[0].id); })();
  }, [ccps, ccpId]);

  const ccp = ccps.find((c) => c.id === ccpId);
  const numericFields = (ccp?.fields || []).filter((f) => NUMERIC_TYPES.includes(f.type));
  const isCustom = (ccp?.fields?.length || 0) > 0;

  useEffect(() => {
    (async () => { setFieldId(numericFields[0]?.id || ""); })();
  }, [ccpId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ccpId) return;
    (async () => {
      setLoading(true);
      try {
        const l = await getHaccpLogs({ ccp_id: ccpId });
        setLogs([...l].reverse());
      } finally {
        setLoading(false);
      }
    })();
  }, [ccpId]);

  const activeField = numericFields.find((f) => f.id === fieldId);
  // Limits + value extraction depend on custom vs legacy
  const limits = isCustom
    ? {
        critical_min: activeField?.critical_min,
        critical_max: activeField?.critical_max,
        warning_min: activeField?.warning_min,
        warning_max: activeField?.warning_max,
        unit: activeField?.unit || "",
        label: activeField?.name || "",
      }
    : {
        critical_min: ccp?.critical_min,
        critical_max: ccp?.critical_max,
        warning_min: ccp?.warning_min,
        warning_max: ccp?.warning_max,
        unit: ccp?.unit || "",
        label: ccp?.name || "",
      };

  const data = logs
    .map((l) => {
      const raw = isCustom ? l.field_values?.[fieldId] : l.value;
      const v = raw === undefined || raw === "" || raw === null ? null : Number(raw);
      return {
        t:
          new Date(l.timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
          " " +
          new Date(l.timestamp).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
        value: v,
      };
    })
    .filter((d) => d.value != null && !Number.isNaN(d.value));

  const noNumeric = isCustom && numericFields.length === 0;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="text-base">{t("haccp.trendTitle")}</CardTitle>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="w-full sm:w-64">
            <Select value={ccpId} onValueChange={setCcpId}>
              <SelectTrigger data-testid="trends-ccp-select"><SelectValue placeholder={t("haccp.selectACcp")} /></SelectTrigger>
              <SelectContent>{ccps.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isCustom && numericFields.length > 0 && (
            <div className="w-full sm:w-48">
              <Select value={fieldId} onValueChange={setFieldId}>
                <SelectTrigger data-testid="trends-field-select"><SelectValue placeholder={t("prod.field")} /></SelectTrigger>
                <SelectContent>{numericFields.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!ccp ? (
          <p className="py-12 text-center text-sm text-muted-foreground">{t("haccp.selectToView")}</p>
        ) : noNumeric ? (
          <p className="py-12 text-center text-sm text-muted-foreground">{t("haccp.noNumeric")}</p>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-center">
            <Clock className="h-10 w-10 text-muted-foreground/50" />
            <p className="mt-3 text-sm text-muted-foreground">{loading ? t("common.loading") : t("haccp.noReadingsFor", { ccp: ccp.name })}</p>
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span>{t("haccp.criticalRange", { min: limits.critical_min ?? "–", max: limits.critical_max ?? "–", unit: limits.unit })}</span>
              {(limits.warning_min != null || limits.warning_max != null) && (
                <span>{t("haccp.warningRange", { min: limits.warning_min ?? "–", max: limits.warning_max ?? "–", unit: limits.unit })}</span>
              )}
              <span>{t("haccp.readingsCount", { n: data.length })}</span>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={data} margin={{ left: -10, right: 12, top: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="t" fontSize={10} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" angle={-12} textAnchor="end" height={50} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
                {limits.critical_max != null && <ReferenceLine y={limits.critical_max} stroke="hsl(var(--danger))" strokeDasharray="5 4" label={{ value: `crit max ${limits.critical_max}`, fontSize: 10, fill: "hsl(var(--danger))", position: "insideTopRight" }} />}
                {limits.critical_min != null && <ReferenceLine y={limits.critical_min} stroke="hsl(var(--danger))" strokeDasharray="5 4" label={{ value: `crit min ${limits.critical_min}`, fontSize: 10, fill: "hsl(var(--danger))", position: "insideBottomRight" }} />}
                {limits.warning_max != null && <ReferenceLine y={limits.warning_max} stroke="hsl(var(--warning))" strokeDasharray="2 4" />}
                {limits.warning_min != null && <ReferenceLine y={limits.warning_min} stroke="hsl(var(--warning))" strokeDasharray="2 4" />}
                <Line type="monotone" dataKey="value" stroke="hsl(var(--brand))" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} name={`${limits.label}${limits.unit ? ` (${limits.unit})` : ""}`} />
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </CardContent>
    </Card>
  );
}
